import React from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Tooltip, Tag, TagLabel, TagLeftIcon, Text } from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { Span } from '../Typography';
import RepeatIcon from '../Icons/RepeatIcon';
import { getOptimizedImageURL } from '../../subscriptions/utils';

const VariantImage = styled.img`
  width: 64px;
  border-radius: 8px;
  position: relative;
  min-width: 64px;
`;

const scaleUp = keyframes`
  from{
    transform: scale(0);
    opacity: 1;
  }
 to{
    transform: scale(1);
    opacity: 1;
  }
`;

const scaleDown = keyframes`
  from{
    transform: scale(1);
    opacity: 1;
  }
 to{
    transform: scale(0);
    opacity: 1;
  }
`;

export const ImageEmptyState = styled.div<{ width?: string; height?: string }>`
  width: ${({ width }) => width ?? '64px'};
  height: ${({ height }) => height ?? '64px'};
  border-radius: 8px;
  background-color: #dfe3e8;
  position: relative;
  min-width: 32px;
  &:empty {
    display: block;
  }
`;

const ImageContainer = styled.div<{ withBadge?: boolean }>`
  cursor: pointer;
  position: relative;
  padding-top: ${({ withBadge }) => (withBadge ? '10px' : '0')};
  padding-right: 16px;
  display: inline-block;
`;

const Badge = styled.div<{ isShown?: boolean }>`
  position: absolute;
  right: 10px;
  top: 2px;
  background: #000000;
  text-align: center;
  border-radius: 50%;
  color: white;
  font-size: 12px;
  width: 16px;
  line-height: 16px;
  height: 16px;
  z-index: 10;
  font-weight: bold;
  animation: ${({ isShown }) => (isShown ? scaleUp : scaleDown)} 0.2s
    cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
`;

const OneTimeAddonLabel = styled(Span)`
  font-size: 12px;
`;

const ProductDetailContainer = styled.div`
  display: flex;
  flex-direction: row;
`;
const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProductName = styled.h2`
  cursor: pointer;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 2px;
`;

const Subtitle = styled.p<{ subdued?: boolean }>`
  cursor: pointer;
  font-size: 16px;
  font-weight: 400;
  color: ${({ subdued }) => (subdued ? '#637381' : '#000000')};
  margin-bottom: 2px;
`;

const Annotation = styled.p<{ subdued?: boolean }>`
  font-size: 12px;
  font-weight: normal;
  color: ${({ subdued }) => (subdued ? '#637381' : '#000000')};
`;

const RepeatIconStyles = styled(RepeatIcon)`
  display: inline-flex;
  align-self: center;
  position: relative;
  margin-right: 10px;
  width: 16px;
  height: 16px;
`;

const ProductDetail = ({
  title,
  subtitle,
  image,
  annotation = null,
  imgBadgeText,
  onItemClick,
  outOfStockItems,
  tooltipLabel,
  prepaidV2Terms,
  isAutorenewLabel,
  isOneTimeAddonLabel,
}): React.ReactElement => {
  return (
    <RowContainer className="awt-row-container">
      <ProductDetailContainer
        className="bundle-product-detail-container"
        data-testid="bundle-product-detail-container"
      >
        <ImageContainer
          className="bundle-img-container"
          data-testid="bundle-image-container"
          withBadge={!!imgBadgeText}
          onClick={onItemClick}
        >
          <Badge className="bundle-img-badge" isShown={imgBadgeText}>
            {imgBadgeText}
          </Badge>
          {image ? (
            <VariantImage
              className="bundle-variant-img"
              alt={title}
              src={getOptimizedImageURL({
                url: image,
                width: '128',
              })}
            />
          ) : (
            <ImageEmptyState className="bundle-img-empty-state" />
          )}
        </ImageContainer>
        <div className="bundle-product-details">
          <ProductName
            className="bundle-product-name"
            data-testid="bundle-product-name"
            onClick={onItemClick}
          >
            {title}
          </ProductName>
          <Subtitle
            className="bundle-product-subtitle"
            onClick={onItemClick}
            subdued
          >
            {subtitle} {outOfStockItems && <span>{outOfStockItems}</span>}
          </Subtitle>

          {!isOneTimeAddonLabel ? (
            <Annotation className="bundle-product-annotation">
              <>
                <Tooltip hasArrow label={tooltipLabel} placement="top-start">
                  <span className="bundle-repeat-icon-wrapper">
                    <RepeatIconStyles className="bundle-repeat-icon" />
                  </span>
                </Tooltip>

                {annotation && (
                  <span dangerouslySetInnerHTML={{ __html: annotation }}></span>
                )}
              </>
            </Annotation>
          ) : (
            <OneTimeAddonLabel modifiers={['subdued']}>
              {isOneTimeAddonLabel}
            </OneTimeAddonLabel>
          )}
        </div>
      </ProductDetailContainer>
      {prepaidV2Terms && (
        <Tag
          className="awt-prepaid-terms-tag"
          size="lg"
          colorScheme="gray"
          borderRadius="full"
          padding="8px"
          maxW="100%"
          width="fit-content"
        >
          <TagLabel fontSize="12px">{prepaidV2Terms}</TagLabel>
          {isAutorenewLabel && (
            <Tag
              className="awt-autorenew-on-tag"
              size="sm"
              colorScheme="gray"
              background="white"
              borderRadius="full"
              marginLeft="8px"
            >
              <TagLeftIcon as={CheckIcon} />
              <TagLabel>
                <Text fontSize="sm" marginTop="4px" marginBottom="4px">
                  {isAutorenewLabel}
                </Text>
              </TagLabel>
            </Tag>
          )}
        </Tag>
      )}
    </RowContainer>
  );
};

export default ProductDetail;
